import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { RoutesList } from '../../../config/routes-list';
import { useNotification } from '../../../hooks/use-notification';
import { useToday } from '../../../hooks/use-today';
import { formatDateForInput } from '../../../utils/date-utils';
import {
  useUpdateItemStatus,
  useUploadLabelPhoto,
  useValidateItem,
} from './use-delivery-note-mutations';

export type ItemStatus = 'ACCEPTED' | 'ACCEPTED_WITH_RESERVATION' | 'REJECTED';

interface ItemValidationFormState {
  batchNumber: string;
  expiryDate: string;
  /** True for shelf-stable products; allows skipping the expiry date. */
  hasNoExpiry: boolean;
  status: ItemStatus;
  statusReason: string;
  labelPhotos: File[];
}

interface DeliveryNoteItem {
  id: string;
  productName: string;
  batchNumber?: string | null;
  expiryDate?: string | null;
  hasNoExpiry?: boolean;
  status?: string | null;
  statusReason?: string | null;
}

function isValidItemStatus(status: string | null | undefined): status is ItemStatus {
  return status === 'ACCEPTED' || status === 'ACCEPTED_WITH_RESERVATION' || status === 'REJECTED';
}

function getDefaultExpiryDate(today: Date): string {
  return formatDateForInput(today);
}

export function useItemValidationForm(
  itemId: string | undefined,
  item: DeliveryNoteItem | undefined,
) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  const today = useToday();
  const validateItem = useValidateItem();
  const updateItemStatus = useUpdateItemStatus();
  const uploadLabelPhoto = useUploadLabelPhoto();

  const [formState, setFormState] = useState<ItemValidationFormState>({
    batchNumber: '',
    expiryDate: getDefaultExpiryDate(today),
    hasNoExpiry: false,
    status: 'ACCEPTED',
    statusReason: '',
    labelPhotos: [],
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (item) {
      const hasNoExpiry = item.hasNoExpiry === true;
      setFormState({
        batchNumber: item.batchNumber || '',
        expiryDate: hasNoExpiry ? '' : item.expiryDate || getDefaultExpiryDate(today),
        hasNoExpiry,
        status: isValidItemStatus(item.status) ? item.status : 'ACCEPTED',
        statusReason: item.statusReason || '',
        labelPhotos: [],
      });
    }
  }, [item, today]);

  const updateField = <K extends keyof ItemValidationFormState>(
    field: K,
    value: ItemValidationFormState[K],
  ) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
  };

  // An item is traceable when a batch number is present AND either the expiry
  // date is filled OR the operator has explicitly marked the product as
  // shelf-stable (hasNoExpiry). The XOR is enforced server-side too.
  const isTraceabilityComplete =
    formState.batchNumber.trim() !== '' &&
    (formState.hasNoExpiry === true || formState.expiryDate !== '');

  const requiresReason =
    formState.status === 'ACCEPTED_WITH_RESERVATION' || formState.status === 'REJECTED';

  const canSubmit =
    isTraceabilityComplete && (!requiresReason || formState.statusReason.trim() !== '');

  const navigateToItemsList = () => {
    navigate(RoutesList.GOODS_RECEIVING_REGISTER_ITEMS);
  };

  async function uploadPhotos(itemId: string, photos: File[]) {
    const results = await Promise.allSettled(
      photos.map((photo) => uploadLabelPhoto.mutateAsync({ itemId, file: photo })),
    );
    const failed = results.filter((r) => r.status === 'rejected');
    if (failed.length > 0) {
      throw new Error(`${failed.length}/${photos.length} photo uploads failed`);
    }
  }

  const handleSubmit = async () => {
    if (!canSubmit || !itemId) {
      return;
    }

    setIsSubmitting(true);
    try {
      await validateItem.mutateAsync({
        itemId,
        data: {
          batchNumber: formState.batchNumber.trim(),
          expiryDate: formState.hasNoExpiry ? null : formState.expiryDate || null,
          hasNoExpiry: formState.hasNoExpiry,
        },
      });

      if (formState.labelPhotos.length > 0) {
        await uploadPhotos(itemId, formState.labelPhotos);
      }

      await updateItemStatus.mutateAsync({
        itemId,
        data: {
          status: formState.status,
          reason: requiresReason ? formState.statusReason.trim() : undefined,
        },
      });

      showSuccess(t('goodsReceiving.itemValidatedSuccess'));
      navigateToItemsList();
    } catch {
      showError(t('goodsReceiving.itemValidationError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    formState,
    updateField,
    isTraceabilityComplete,
    requiresReason,
    canSubmit,
    isSubmitting,
    handleSubmit,
    navigateToItemsList,
  };
}
